import React, { useRef } from 'react'
import { Dialog } from 'primereact/dialog'
import { Toast } from 'primereact/toast'
import SearchHeader from './components/SearchHeader/SearchHeader'
import SearchResultsPanel from './components/SearchResultsPanel/SearchResultsPanel'
import ConfirmContributorDetails from './components/ConfirmContributorDetails/ConfirmContributorDetails'
import AssociatedNamesPanel from './components/AssociatedNamesPanel/AssociatedNamesPanel'
import MembershipsPanel from './components/MembershipsPanel/MembershipsPanel'
import WorksPanel from './components/WorksPanel/WorksPanel'
import ModalFooter from './components/ModalFooter/ModalFooter'
import useContactSearch from './hooks/useContactSearch'
import ContactSearchApi from 'api/DataEntry/ContactSearch'
import {
    setContactSearchStatusAction,
    setContactSearchResultsAction
} from './store/actions'
import { selectContactSearchFetchRequest } from './store/selectors'
import './ContactSearchSelectModal.scss'

const ContactSearchSelectModal = ({
    visible,
    onHide,
    onConfirm,
    onPotentialMemberCreated
}) => {
    const {
        // Search state
        searchResults,
        totalCount,
        isLoading,
        hasSearched,

        // Selection state
        selectedContact,
        selectedName,
        selectedMembership,

        // Handlers
        handleSearch,
        handleContactSelect,
        handleNameSelect,
        handleMembershipSelect,
        handleClearSearch
    } = useContactSearch(visible)

    const searchInputRef = useRef(null)
    const toastRef = useRef(null)

    const handleConfirm = () => {
        if (selectedContact && selectedName && onConfirm) {
            onConfirm({
                contact: selectedContact,
                name: selectedName,
                membership: selectedMembership
            })
            onHide()
        }
    }

    const handlePotentialMemberCreated = (newContact) => {
        if (onPotentialMemberCreated) {
            onPotentialMemberCreated(newContact)
        }
    }

    const footer = (
        <ModalFooter
            onCancel={onHide}
            onConfirm={handleConfirm}
            selectedContact={selectedContact}
            selectedName={selectedName}
            selectedMembership={selectedMembership}
        />
    )

    return (
        <>
        <Toast ref={toastRef} />
        <Dialog
            header="Add contributor to episode"
            visible={visible}
            style={{ width: '95vw', height: '90vh' }}
            onHide={onHide}
            onShow={() => searchInputRef.current?.focus()}
            modal
            draggable={false}
            maximizable
            footer={footer}
            className="contact-search-select-modal"
            closable
            closeOnEscape
        >
            <ContactSearchApi
                setStatusAction={setContactSearchStatusAction}
                setResultsAction={setContactSearchResultsAction}
                requestSelector={selectContactSearchFetchRequest}
            />
            <div className="modal-content container-fluid d-flex flex-column flex-grow-1">
                <div className="row flex-grow-1 main-layout">
                    {/* Left Column */}
                    <div className="col-5 d-flex flex-column left-column">
                        <SearchHeader
                            inputRef={searchInputRef}
                            onSearch={handleSearch}
                            onPotentialMemberCreated={handlePotentialMemberCreated}
                            toastRef={toastRef}
                            isLoading={isLoading}
                        />

                        {/* No results message */}
                        {hasSearched && !isLoading && searchResults.length === 0 && (
                            <div className="no-results-message">
                                <strong>Results:</strong> There are no results that match your search criteria.
                            </div>
                        )}

                        {/* Loading state */}
                        {isLoading && (
                            <div className="loading-message">
                                <i className="pi pi-spin pi-spinner" /> Searching...
                            </div>
                        )}

                        {/* Search Results */}
                        {hasSearched && searchResults.length > 0 && (
                            <SearchResultsPanel
                                searchResults={searchResults}
                                totalCount={totalCount}
                                selectedContact={selectedContact}
                                onContactSelect={handleContactSelect}
                            />
                        )}
                    </div>

                    {/* Right Column - Only show when there are results */}
                    {hasSearched && !isLoading && searchResults.length > 0 && (
                        <div className="col-7 d-flex flex-column right-column">
                            <ConfirmContributorDetails
                                authorKey={selectedContact?.authorKey}
                                name={selectedName?.name}
                                membershipType={selectedMembership?.membershipType}
                                membershipTypeId={selectedMembership?.membershipTypeId}
                            />

                            <AssociatedNamesPanel
                                names={selectedContact?.names || null}
                                selectedName={selectedName}
                                onNameSelect={handleNameSelect}
                            />

                            <MembershipsPanel
                                memberships={selectedContact?.memberships || null}
                                selectedMembership={selectedMembership}
                                onMembershipSelect={handleMembershipSelect}
                                contactTypeId={selectedContact?.contactTypeId}
                            />

                            <WorksPanel
                                works={selectedContact?.works || null}
                                contactId={selectedContact?.contactId}
                                lazyLoadWorks={selectedContact?.lazyLoadWorks || false}
                                worksCount={selectedContact?.worksCount || 0}
                            />
                        </div>
                    )}
                </div>
            </div>
        </Dialog>
        </>
    )
}

export default ContactSearchSelectModal
